import type { SpecValueKind } from '@inference.engine/ad-formats/web';

/** Drawn to scale against the format's canvas. */
export type SpatialDiagram = 'size' | 'ratio' | 'ratioList' | 'safeZone';

/** A fixed illustration of what a non-spatial property describes. */
export type StaticDiagram =
  | 'file'
  | 'color'
  | 'duration'
  | 'frameRate'
  | 'bitrate'
  | 'audio'
  | 'cropping'
  | 'thumbnail';

export type SpecDiagramKind =
  | { type: 'spatial'; diagram: SpatialDiagram }
  | { type: 'static'; diagram: StaticDiagram };

export function diagramFor(kind: SpecValueKind, property: string): SpecDiagramKind {
  if (kind === 'size') return { type: 'spatial', diagram: 'size' };
  if (kind === 'ratio') return { type: 'spatial', diagram: 'ratio' };
  if (kind === 'ratioList') return { type: 'spatial', diagram: 'ratioList' };
  if (kind === 'margins') return { type: 'spatial', diagram: 'safeZone' };

  const family = (diagram: StaticDiagram): SpecDiagramKind => ({ type: 'static', diagram });
  if (property.startsWith('thumbnail.')) return family('thumbnail');
  if (property.startsWith('audio.')) return family('audio');
  if (property.startsWith('duration.')) return family('duration');
  if (property.startsWith('frameRate.')) return family('frameRate');
  if (property.startsWith('bitrate.')) return family('bitrate');
  if (property === 'cropping') return family('cropping');
  if (['colorSpace', 'pixelFormat', 'chromaSubsampling', 'transparency'].includes(property))
    return family('color');
  return family('file');
}

/** `9:16` as width over height, or undefined when it is not a ratio. */
export function ratioValue(ratio: string): number | undefined {
  const [w, h] = ratio.split(':').map(Number);
  return w && h && Number.isFinite(w / h) ? w / h : undefined;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The largest rect of `aspect` (width / height) that fits `outer`, centred in it. */
export function fitAspect(outer: Rect, aspect: number): Rect {
  const width = Math.min(outer.width, outer.height * aspect);
  const height = width / aspect;
  return {
    x: outer.x + (outer.width - width) / 2,
    y: outer.y + (outer.height - height) / 2,
    width,
    height,
  };
}
